import "./init";
import { App, ConfigProvider, Select, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { createIntl, createIntlCache, RawIntlProvider } from "react-intl";
import './standalone-wrapper.less';
import { ConfigProviderProps } from "antd/es/config-provider";

type StandaloneWrapperProps = {
    children?: React.ReactNode;
    antConfig?: ConfigProviderProps;
    extraTranslations?: ExtraTranslations;
    translationPrefix?: string;
}

type ExtraTranslations = {
    [key: string]: {
        [lang: string]: string;
    }
}

export const getExtraTranslationsForLang = (extraTranslations: ExtraTranslations, lang: string, prefix: string) => {
    return Object.keys(extraTranslations).reduce((acc, key) => {
        return {
            ...acc,
            [`${prefix}.${key}`]: extraTranslations[key][lang.toUpperCase()]
        }
    }, {})
}


const API_URL = import.meta.env.VITE_API_URL;
const l18n_URL = import.meta.env.VITE_LANGUAGE_URL;



const LanguageSelect = (props: { lang: string, onChange: (newLang: string) => void }) => {

    return (
        <Select
            defaultValue={props.lang}
            style={{ width: 120 }}
            onChange={props.onChange}
            value={props.lang}
            options={[
                {
                    label: 'English',
                    value: 'en'
                },
                {
                    label: 'Polish',
                    value: 'pl'
                }
            ]}
        />
    )

}

const StandaloneWrapper = (props: StandaloneWrapperProps) => {

    const [lang, setLang] = useState<string>('pl');
    const [intl, setIntl] = useState<any>(null);
    const [baseMessages, setBaseMessages] = useState<any>(null);

    const cache = useMemo(() => createIntlCache(), []);

    const downloadLanguageFile = async (lang: string) => {
        const res = await fetch(`${l18n_URL}${lang}.json`, { cache: "reload" });
        const messages = await res.json();
        setBaseMessages(messages);
        return messages;
    }

    const updateIntl = (baseMessages: any, extraTranslations?: ExtraTranslations, translationPrefix?: string) => {
        const extraMessages = extraTranslations ? getExtraTranslationsForLang(extraTranslations, lang, translationPrefix || "") : {};

        const combinedMessages = {
            ...baseMessages,
            ...extraMessages
        }

        setIntl(createIntl({
            locale: lang,
            messages: combinedMessages
        }, cache));
    }

    const changeLang = async (lang: string) => {
        const messages = await downloadLanguageFile(lang);
        setLang(lang);
        updateIntl(messages, props.extraTranslations, props.translationPrefix);
    }

    // Download language file when language changes
    useEffect(() => {
        if (!lang || !lang.length) return;
        changeLang(lang);
    }, [lang]);

    // Update intl when extraTranslations change (without downloading language file)
    useEffect(() => {
        if (baseMessages && props.extraTranslations !== undefined) {
            updateIntl(baseMessages, props.extraTranslations, props.translationPrefix);
        }
    }, [props.extraTranslations, props.translationPrefix]);


    const parentComms = (event: MessageEvent) => {
        // console.log('event', event);

        if (event?.data) {
            if (event.data.lang) {
                setLang(event.data.lang)
            }
        }


    }

    useEffect(() => {

        const debugTranslation = localStorage.getItem("translationDebug");
        window.debugTranslation = debugTranslation === "true";

        window.addEventListener('message', parentComms);

        return () => {
            window.removeEventListener('message', parentComms);
        }
    }, []);

    if (!intl) return <div style={{ width: '100vw', height: '100vh', display: 'grid', placeItems: 'center' }}><Spin /></div>;

    return (
            <RawIntlProvider
                key={lang}
                value={intl}
            >
                <ConfigProvider
                    {...props.antConfig}
                    theme={{
                        cssVar: true
                    }}
                >
                    <App>
                        <div className="standalone-wrapper">
                        {props.children}
                        <div className="standalone-tools">
                            <LanguageSelect lang={lang} onChange={setLang} />
                        </div>
                        </div>
                    </App>
                </ConfigProvider>
            </RawIntlProvider>
    )

}

export default StandaloneWrapper;